// =====================================
// CORE ENTITY TYPES
// =====================================

export interface User {
  id: string;
  email?: string;
  display_name: string;
  avatar_url?: string;
  default_currency?: string;
  timezone?: string;
  // Period settings: week start 0=Sun..6=Sat (null = locale default), accounting month start day 1-28
  week_start_day?: number | null;
  month_start_day?: number | null;
  created_at: string;
  updated_at?: string;
  is_virtual?: boolean;
  created_by_user_id?: string;
  claimed_by_user_id?: string;
  claimed_at?: string;
  subscription_tier?: SubscriptionTier;
  subscription_expires_at?: string;
  buh_id?: string | null;
  billing_user_id?: string;
  trial_ends_at?: string;
  owned_groups_count?: number;
  payment_methods?: PaymentMethod[];
  payment_qr_enabled?: boolean;
}

export interface Group {
  id: string;
  name: string;
  description?: string;
  currency_code: string;
  created_by: string;
  created_at: string;
  updated_at?: string;
  invite_code?: string;
  invite_code_expires_at?: string | null;
  invite_code_uses_count?: number;
  invite_code_max_uses?: number | null;
  color?: string;
  budget_enabled?: boolean;
  budget_amount_minor?: number | null;
  budget_period?: 'monthly' | 'total' | null;
  budget_alert_threshold?: number | null;
  // Day (1-28) the monthly budget period starts; 1 = calendar month
  budget_start_day?: number | null;
  // true = group admins may edit/delete any expense; false = creator and payer only
  admins_can_edit_expenses?: boolean;
}

export interface GroupMember {
  id: string;
  group_id: string;
  user_id: string;
  role: 'admin' | 'member';
  joined_at: string;
  display_order?: number | null;
  // Per-user, per-group archive flag. Archiving hides the group from this user's
  // lists/dashboards without affecting other members or the group data itself.
  archived?: boolean | null;
  user?: User;
  group?: Group;
}

export interface Currency {
  code: string;
  symbol: string;
  minor_units: number;
}

export interface ExchangeRate {
  id: string;
  from_currency: string;
  to_currency: string;
  rate: number;
  date: string;
  created_at: string;
}

// =====================================
// EXPENSE TYPES
// =====================================

export interface Expense {
  id: string;
  group_id: string;
  description: string;
  amount_minor_src: number;
  currency_code_src: string;
  amount_minor_usd: number;
  fx_rate_to_usd: number;
  paid_by: string;
  category_id?: string;
  expense_date: string;
  receipt_url?: string;
  notes?: string;
  is_recurring?: boolean;
  recurring_expense_id?: string;
  is_generated?: boolean;
  created_by: string;
  created_at: string;
  updated_at?: string;
  latitude?: number;
  longitude?: number;
  location_name?: string;
  // Display-only fields (from joins)
  group_name?: string;
  paid_by_name?: string;
  category_name?: string;
  category_icon?: string;
  category_color?: string;
  split_type?: SplitType;
  splits?: ExpenseSplit[];
  has_open_dispute?: boolean;
  is_custom_fx_rate?: boolean;
}

export interface ExpenseSplit {
  id: string;
  expense_id: string;
  user_id: string;
  amount_minor: number;
  amount_minor_usd: number;
  split_value?: number;
  user?: User;
}

export type SplitType = 'equal' | 'exact' | 'percentage' | 'shares';

export interface CreateExpenseData {
  groupId: string;
  paidBy: string;
  createdBy: string;
  description: string;
  amount: number;
  currencyCode: string;
  categoryId?: string;
  date?: string;
  receipt_url?: string;
  notes?: string;
  splitType: SplitType;
  splitWith: string[];
  exactAmounts?: Record<string, number>;
  percentages?: Record<string, number>;
  shares?: Record<string, number>;
  customFxRate?: number;
  /** Optional snapshot of participant display names, keyed by user id. */
  participantDisplayNames?: Record<string, string>;
}

export interface UpdateExpenseData {
  description?: string;
  amount?: number;
  currency?: string;
  categoryId?: string;
  date?: string;
  paidBy?: string;
  splitType?: SplitType;
  splitWith?: string[];
  exactAmounts?: Record<string, number>;
  percentages?: Record<string, number>;
  shares?: Record<string, number>;
  customFxRate?: number;
}

export interface ExpenseFilters {
  startDate?: string;
  endDate?: string;
  categoryId?: string;
  paidBy?: string;
  includesUser?: string;
  minAmount?: number;
  maxAmount?: number;
  limit?: number;
}

export interface SplitCalculationData {
  amount: number;
  splitType: SplitType;
  splitWith: string[];
  exactAmounts?: Record<string, number>;
  percentages?: Record<string, number>;
  shares?: Record<string, number>;
}

// =====================================
// GROUP TYPES
// =====================================

export interface CreateGroupData {
  name: string;
  description?: string;
  currency_code: string;
  color?: string;
}

export interface UpdateGroupData {
  name?: string;
  description?: string;
  baseCurrency?: string;
  color?: string;
  budgetEnabled?: boolean;
  budgetAmountMinor?: number | null;
  budgetPeriod?: 'monthly' | 'total' | null;
  budgetAlertThreshold?: number | null;
  budgetStartDay?: number | null;
  adminsCanEditExpenses?: boolean;
}

// =====================================
// SETTLEMENT TYPES
// =====================================

export interface Settlement {
  id: string;
  group_id: string;
  from_user_id: string;
  to_user_id: string;
  amount_minor: number;
  settlement_date: string;
  description?: string;
  created_by: string;
  created_at: string;
}

export interface GroupBalance {
  id?: string;
  group_id: string;
  user_id: string;
  balance_minor: number;
  currency: string;
  last_calculated?: string;
  user?: User;
}

export interface CreateSettlementData {
  groupId: string;
  fromUserId: string;
  toUserId: string;
  amount: number;
  currency: string;
  note?: string;
}

// =====================================
// CATEGORY TYPES
// =====================================

export interface Category {
  id: string;
  key?: string;
  name: string;
  icon?: string;
  color?: string;
}

// =====================================
// NOTIFICATION TYPES
// =====================================

export interface NotificationData {
  expense_id?: string;
  member_id?: string;
  settlement_id?: string;
  amount?: number;
  currency?: string;
  [key: string]: string | number | boolean | undefined;
}

export interface Notification {
  id: string;
  user_id: string;
  group_id?: string;
  type: string;
  title: string;
  message?: string;
  data?: NotificationData;
  read: boolean;
  created_at: string;
}

export type NotificationType =
  | 'expense_added'
  | 'expense_updated'
  | 'expense_deleted'
  | 'member_joined'
  | 'member_left'
  | 'settlement_made'
  | 'settlement_received'
  | 'group_updated'
  | 'dispute_raised'
  | 'dispute_resolved'
  | 'reminder'
  | 'system';

// =====================================
// AUTH TYPES
// =====================================

export interface CreateUserProfile {
  email: string;
  name: string;
  avatarUrl?: string;
  defaultCurrency?: string;
}

export interface UpdateUserProfile {
  name?: string;
  avatarUrl?: string | null;
  defaultCurrency?: string;
  timezone?: string;
  weekStartDay?: number | null;
  monthStartDay?: number;
  payment_methods?: PaymentMethod[];
  payment_qr_enabled?: boolean;
}

// =====================================
// PREMIUM TYPES
// =====================================

export type SubscriptionTier = 'FREE' | 'TRIAL' | 'MONTHLY' | 'YEARLY' | 'LIFETIME' | 'VOUCHER';

export type PremiumFeature =
  | 'unlimited_groups'
  | 'receipt_ocr'
  | 'recurring_expenses'
  | 'advanced_statistics'
  | 'csv_export'
  | 'custom_categories'
  | 'group_budgets'
  | 'priority_support'
  | 'premium_badge';

// =====================================
// PAYMENT METHOD TYPES
// =====================================

export type PaymentMethodType =
  | 'iban'
  | 'czech_bank'
  | 'uk_bank'
  | 'us_bank'
  | 'au_bank'
  | 'paypal'
  | 'revolut'
  | 'wise'
  | 'venmo'
  | 'zelle'
  | 'cashapp'
  | 'upi'
  | 'pix'
  | 'mbway'
  | 'bizum'
  | 'swish'
  | 'google_pay'
  | 'apple_pay'
  | 'phone'
  | 'other';

export type PaymentMethodCategory = 'bank_transfer' | 'digital_wallet' | 'mobile_payment';

export interface PaymentMethod {
  type: PaymentMethodType;
  value: string;
  label?: string;
  metadata?: Record<string, string>;
  is_primary?: boolean;
  sort_order?: number;
}

// =====================================
// ACTIVITY TYPES
// =====================================

export interface Activity {
  id: string;
  group_id: string;
  user_id: string;
  action: string;
  entity_type: string;
  entity_id: string;
  details?: Record<string, unknown>;
  created_at: string;
  user?: User;
}

// =====================================
// DISPUTE TYPES
// =====================================

export type DisputeReasonType = 'not_present' | 'wrong_amount' | 'wrong_split' | 'duplicate' | 'other';
export type DisputeStatus = 'open' | 'resolved' | 'rejected' | 'withdrawn';
export type ResolutionType = 'expense_edited' | 'rejected' | 'withdrawn';
export type ResolutionReasonType = 'was_present' | 'amount_correct' | 'split_correct' | 'other';

export interface ExpenseDispute {
  id: string;
  expense_id: string;
  group_id: string;
  raised_by: string;
  reason_type: DisputeReasonType;
  reason_text?: string;
  status: DisputeStatus;
  resolved_by?: string;
  resolved_at?: string;
  resolution_type?: ResolutionType;
  resolution_reason_type?: ResolutionReasonType;
  resolution_note?: string;
  reopen_count: number;
  created_at: string;
  raised_by_user?: User;
  resolved_by_user?: User;
}

/**
 * Dispute enum values in display order. Labels are localized through i18n
 * (expenses:dispute.reason.* / dispute.resolution.* / dispute.status.*).
 */
export const DISPUTE_REASON_TYPES: readonly DisputeReasonType[] = [
  'not_present',
  'wrong_amount',
  'wrong_split',
  'duplicate',
  'other',
];

export const RESOLUTION_REASON_TYPES: readonly ResolutionReasonType[] = [
  'was_present',
  'amount_correct',
  'split_correct',
  'other',
];
